// src/components/RecruitmentDashboard.tsx
// Dashboard de reclutamiento para administradores
// Muestra KPIs de reclutadores, vacantes y candidatos

import React, { useEffect, useState } from 'react';
import {
  Users,
  Briefcase,
  UserCheck,
  TrendingUp,
  RefreshCw,
  Trophy,
  Phone,
  AlertCircle,
  ArrowRight,
  CheckCircle,
  Clock,
  XCircle,
} from 'lucide-react';

import {
  getRecruitmentDashboard,
  type RecruitmentDashboardResponse,
  type RecruiterStats,
  type VacancyStats,
} from '@/lib/api';

interface RecruitmentDashboardProps {
  addNotification?: (notification: {
    type: 'success' | 'error' | 'warning' | 'info';
    title: string;
    message?: string;
    duration?: number;
  }) => void;
}

// Status colors and labels
const STATUS_CONFIG: Record<string, { label: string; color: string; icon: React.ReactNode }> = {
  received: { label: 'Recibidos', color: 'bg-blue-500/20 text-blue-400', icon: <Clock size={14} /> },
  screening: { label: 'En Revisión', color: 'bg-yellow-500/20 text-yellow-400', icon: <Clock size={14} /> },
  evaluated: { label: 'Evaluados', color: 'bg-purple-500/20 text-purple-400', icon: <UserCheck size={14} /> },
  interview: { label: 'Entrevista', color: 'bg-indigo-500/20 text-indigo-400', icon: <Users size={14} /> },
  offer: { label: 'Oferta', color: 'bg-teal-500/20 text-teal-400', icon: <TrendingUp size={14} /> },
  hired: { label: 'Contratados', color: 'bg-green-500/20 text-green-400', icon: <CheckCircle size={14} /> },
  rejected: { label: 'Rechazados', color: 'bg-red-500/20 text-red-400', icon: <XCircle size={14} /> },
  withdrawn: { label: 'Retirados', color: 'bg-gray-500/20 text-gray-400', icon: <XCircle size={14} /> },
};

// KPI Card Component
function KPICard({
  title,
  value,
  subtitle,
  icon,
  trend,
}: {
  title: string;
  value: string | number;
  subtitle?: string;
  icon: React.ReactNode;
  trend?: 'up' | 'down' | 'neutral';
}) {
  return (
    <div className="o-card p-4 flex items-start gap-4">
      <div className="p-3 rounded-xl bg-[var(--accent)]/10 text-[var(--accent)]">
        {icon}
      </div>
      <div className="flex-1">
        <p className="text-sm text-[var(--muted)] mb-1">{title}</p>
        <p className="text-2xl font-semibold">{value}</p>
        {subtitle && (
          <p className="text-xs text-[var(--muted)] mt-1">{subtitle}</p>
        )}
      </div>
    </div>
  );
}

// Vacancy Card Component
function VacancyCard({ vacancy }: { vacancy: VacancyStats }) {
  const statusColor = vacancy.status === 'open' 
    ? 'bg-green-500/20 text-green-400' 
    : vacancy.status === 'draft' 
      ? 'bg-yellow-500/20 text-yellow-400'
      : 'bg-gray-500/20 text-gray-400';

  return (
    <div className="o-card p-4">
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1">
          <h3 className="font-medium text-base">{vacancy.title}</h3>
          {vacancy.department && (
            <p className="text-sm text-[var(--muted)]">{vacancy.department}</p>
          )}
        </div>
        <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusColor}`}>
          {vacancy.status}
        </span>
      </div>
      
      {/* Stats */}
      <div className="grid grid-cols-2 gap-3 mb-3">
        <div className="text-center p-2 rounded-lg bg-[var(--surface-2)]">
          <p className="text-xl font-semibold">{vacancy.applications_count}</p>
          <p className="text-xs text-[var(--muted)]">Candidatos</p>
        </div>
        <div className="text-center p-2 rounded-lg bg-[var(--surface-2)]">
          <p className="text-xl font-semibold">
            {vacancy.avg_score !== null ? `${vacancy.avg_score}` : '-'}
          </p>
          <p className="text-xs text-[var(--muted)]">Score Prom.</p>
        </div>
      </div>
      
      {/* Status breakdown */}
      {Object.keys(vacancy.status_breakdown).length > 0 && (
        <div className="flex flex-wrap gap-1 mb-3">
          {Object.entries(vacancy.status_breakdown).map(([status, count]) => (
            <span
              key={status}
              className={`px-2 py-0.5 rounded text-xs flex items-center gap-1 ${STATUS_CONFIG[status]?.color || 'bg-gray-500/20 text-gray-400'}`}
            >
              {STATUS_CONFIG[status]?.icon}
              {count} {STATUS_CONFIG[status]?.label || status}
            </span>
          ))}
        </div>
      )}
      
      {/* Recruiter */}
      <div className="border-t border-[var(--border)] pt-3">
        {vacancy.recruiter ? (
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-[var(--accent)]/20 flex items-center justify-center text-[var(--accent)]">
              <Users size={14} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">{vacancy.recruiter.name || 'Sin nombre'}</p>
              {vacancy.recruiter.phone && (
                <p className="text-xs text-[var(--muted)] flex items-center gap-1">
                  <Phone size={10} />
                  {vacancy.recruiter.phone}
                </p>
              )}
            </div>
          </div>
        ) : (
          <div className="flex items-center gap-2 text-[var(--muted)]">
            <AlertCircle size={14} />
            <span className="text-sm">Sin reclutador asignado</span>
          </div>
        )}
      </div>
    </div>
  );
}

// Recruiter Card Component
function RecruiterCard({ recruiter }: { recruiter: RecruiterStats }) {
  const conversionRate = recruiter.assigned_candidates > 0
    ? Math.round((recruiter.hired / recruiter.assigned_candidates) * 100)
    : 0;

  return (
    <div className="o-card p-4">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-full bg-[var(--accent)]/20 flex items-center justify-center text-[var(--accent)] font-semibold">
          {recruiter.name.charAt(0).toUpperCase()}
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-medium truncate">{recruiter.name}</h3>
          <p className="text-xs text-[var(--muted)] flex items-center gap-1">
            <Phone size={10} />
            {recruiter.phone || 'Sin teléfono'}
          </p>
        </div>
      </div>
      
      <div className="grid grid-cols-2 gap-2">
        <div className="text-center p-2 rounded-lg bg-[var(--surface-2)]">
          <p className="text-lg font-semibold">{recruiter.assigned_vacancies}</p>
          <p className="text-xs text-[var(--muted)]">Vacantes</p>
        </div>
        <div className="text-center p-2 rounded-lg bg-[var(--surface-2)]">
          <p className="text-lg font-semibold">{recruiter.assigned_candidates}</p>
          <p className="text-xs text-[var(--muted)]">Candidatos</p>
        </div>
        <div className="text-center p-2 rounded-lg bg-yellow-500/10">
          <p className="text-lg font-semibold text-yellow-400">{recruiter.in_process}</p>
          <p className="text-xs text-[var(--muted)]">En Proceso</p>
        </div>
        <div className="text-center p-2 rounded-lg bg-green-500/10">
          <p className="text-lg font-semibold text-green-400">{recruiter.hired}</p>
          <p className="text-xs text-[var(--muted)]">Contratados</p>
        </div>
      </div>
      
      {/* Conversion rate bar */}
      <div className="mt-3">
        <div className="flex justify-between text-xs text-[var(--muted)] mb-1">
          <span>Tasa de conversión</span>
          <span>{conversionRate}%</span>
        </div>
        <div className="h-2 bg-[var(--surface-2)] rounded-full overflow-hidden">
          <div
            className="h-full bg-[var(--accent)] rounded-full transition-all"
            style={{ width: `${conversionRate}%` }}
          />
        </div>
      </div>
    </div>
  );
}

export function RecruitmentDashboard({ addNotification }: RecruitmentDashboardProps) {
  const [data, setData] = useState<RecruitmentDashboardResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [refreshing, setRefreshing] = useState(false);

  const fetchData = async (showRefresh = false) => {
    if (showRefresh) setRefreshing(true);
    else setLoading(true);
    setError('');

    try {
      const result = await getRecruitmentDashboard();
      setData(result);
    } catch (e: any) {
      const errorMsg = e.message || 'Error al cargar datos';
      setError(errorMsg);
      addNotification?.({
        type: 'error',
        title: 'Error',
        message: errorMsg,
        duration: 5000,
      });
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <RefreshCw className="animate-spin text-[var(--accent)]" size={32} />
      </div>
    );
  }

  if (error && !data) {
    return (
      <div className="flex flex-col items-center justify-center h-[60vh] gap-4">
        <AlertCircle size={48} className="text-red-400" />
        <p className="text-[var(--muted)]">{error}</p>
        <button onClick={() => fetchData()} className="o-btn o-btn--primary">
          Reintentar
        </button>
      </div>
    );
  }

  if (!data) return null;

  // Calculate pipeline stats
  const pipelineFlow = [
    { key: 'received', count: data.by_status.received || 0 },
    { key: 'screening', count: data.by_status.screening || 0 },
    { key: 'evaluated', count: data.by_status.evaluated || 0 },
    { key: 'interview', count: data.by_status.interview || 0 },
    { key: 'offer', count: data.by_status.offer || 0 },
    { key: 'hired', count: data.by_status.hired || 0 },
  ];

  const openVacancies = data.vacancies.filter(v => v.status === 'open').length;
  const hiredTotal = data.by_status.hired || 0;
  const inPipelineTotal = Object.entries(data.by_status)
    .filter(([k]) => !['hired', 'rejected', 'withdrawn'].includes(k))
    .reduce((sum, [, v]) => sum + v, 0);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Dashboard de Reclutamiento</h1>
          <p className="text-[var(--muted)]">Vista general de vacantes, candidatos y reclutadores</p>
        </div>
        <button
          onClick={() => fetchData(true)}
          disabled={refreshing}
          className="o-btn o-btn--ghost flex items-center gap-2"
        >
          <RefreshCw size={16} className={refreshing ? 'animate-spin' : ''} />
          Actualizar
        </button>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <KPICard
          title="Total Candidatos"
          value={data.total_applications}
          subtitle={`${inPipelineTotal} en pipeline activo`}
          icon={<Users size={24} />}
        />
        <KPICard
          title="Vacantes Abiertas"
          value={openVacancies}
          subtitle={`${data.vacancies.length} vacantes totales`}
          icon={<Briefcase size={24} />}
        />
        <KPICard
          title="Score Promedio AI"
          value={data.avg_ai_score !== null ? `${data.avg_ai_score}/100` : 'N/A'}
          subtitle="De candidatos evaluados"
          icon={<TrendingUp size={24} />}
        />
        <KPICard
          title="Contratados"
          value={hiredTotal}
          subtitle={`${data.recruiters.length} reclutadores activos`}
          icon={<Trophy size={24} />}
        />
      </div>

      {/* Pipeline Flow */}
      <div className="o-card p-4">
        <h2 className="text-lg font-semibold mb-4">Pipeline de Candidatos</h2>
        <div className="flex items-center justify-between overflow-x-auto pb-2">
          {pipelineFlow.map((stage, i) => (
            <React.Fragment key={stage.key}>
              <div className="flex flex-col items-center min-w-[80px]">
                <div className={`w-12 h-12 rounded-full flex items-center justify-center text-lg font-semibold ${STATUS_CONFIG[stage.key]?.color || 'bg-gray-500/20'}`}>
                  {stage.count}
                </div>
                <span className="text-xs text-[var(--muted)] mt-2 text-center">
                  {STATUS_CONFIG[stage.key]?.label || stage.key}
                </span>
              </div>
              {i < pipelineFlow.length - 1 && (
                <ArrowRight size={20} className="text-[var(--muted)] flex-shrink-0 mx-1" />
              )}
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* Vacancies Section */}
      <div>
        <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <Briefcase size={20} />
          Vacantes ({data.vacancies.length})
        </h2>
        {data.vacancies.length === 0 ? (
          <div className="o-card p-8 text-center text-[var(--muted)]">
            <Briefcase size={48} className="mx-auto mb-4 opacity-50" />
            <p>No hay vacantes registradas</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {data.vacancies.map((v) => (
              <VacancyCard key={v.id} vacancy={v} />
            ))}
          </div>
        )}
      </div>

      {/* Recruiters Section */}
      <div>
        <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <UserCheck size={20} />
          Reclutadores ({data.recruiters.length})
        </h2>
        {data.recruiters.length === 0 ? (
          <div className="o-card p-8 text-center text-[var(--muted)]">
            <Users size={48} className="mx-auto mb-4 opacity-50" />
            <p>No hay reclutadores con vacantes o candidatos asignados</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {data.recruiters.map((r) => (
              <RecruiterCard key={r.id} recruiter={r} />
            ))}
          </div>
        )}
      </div>

      {/* Top Candidates */}
      {data.top_candidates.length > 0 && (
        <div>
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <Trophy size={20} />
            Top Candidatos
          </h2>
          <div className="o-card overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="bg-[var(--surface-2)]">
                  <th className="text-left p-3 text-sm font-medium text-[var(--muted)]">#</th>
                  <th className="text-left p-3 text-sm font-medium text-[var(--muted)]">Nombre</th>
                  <th className="text-center p-3 text-sm font-medium text-[var(--muted)]">Score AI</th>
                  <th className="text-center p-3 text-sm font-medium text-[var(--muted)]">Estado</th>
                </tr>
              </thead>
              <tbody>
                {data.top_candidates.map((c, i) => (
                  <tr key={c.id} className="border-t border-[var(--border)]">
                    <td className="p-3">
                      <span className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-sm font-semibold ${
                        i === 0 ? 'bg-yellow-500/20 text-yellow-400' :
                        i === 1 ? 'bg-gray-400/20 text-gray-400' :
                        i === 2 ? 'bg-amber-700/20 text-amber-600' :
                        'bg-[var(--surface-2)] text-[var(--muted)]'
                      }`}>
                        {i + 1}
                      </span>
                    </td>
                    <td className="p-3 font-medium">{c.name}</td>
                    <td className="p-3 text-center">
                      <span className="px-2 py-1 rounded bg-[var(--accent)]/20 text-[var(--accent)] font-semibold">
                        {c.score}
                      </span>
                    </td>
                    <td className="p-3 text-center">
                      <span className={`px-2 py-1 rounded text-xs ${STATUS_CONFIG[c.status]?.color || 'bg-gray-500/20'}`}>
                        {STATUS_CONFIG[c.status]?.label || c.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

export default RecruitmentDashboard;
